// ─── HADIAH PEMBELIAN di pesanan — helper MURNI ──────────────────────────────
//
// Tahap 4A (mig 8850, RENCANA-hadiah-pembelian §16): hadiah TERIKAT ke barang
// syaratnya. Tak ada lagi Lepas Hadiah; Edit Pesanan, Ubah Pcs, dan batal atas
// permintaan pembeli menggeser hadiah sebesar perubahan hak. Tahap 4B (mig 8860):
// barang tak terikat di pesanan yang sama ikut pembelian berhadiahnya, atau jadi
// pembelian baru otomatis. Server penentunya; helper di sini cermin rumusnya
// untuk PRATINJAU di layar:
//   • `giftPurchaseLive`   ↔ `gift_purchase_live` (hak & pcs hidup);
//   • `giftAdjustTarget`   ↔ rumus geser `gift_adjust`;
//   • `giftRebindQuotas`   ↔ pembagian per varian `gift_rebind_order`
//     (+ sisa tak terikat → `gift_auto_attach_order` langkah a);
//   • `giftAdjustNoteText` ↔ catatan `order_gifts.adjust_note`;
//   • `giftFillResultText` ↔ hasil Lengkapi Hadiah `admin_fill_order_gift` (5-2).
// Teks di berkas ini dipakai pembeli DAN admin → tanpa kata ganti.

import {
  giftEntitlementMeasure,
  mapGiftRuleRow,
  variantFitsGiftFilter,
  type GiftCoverRule,
  type GiftMeasureRule
} from "@/lib/gift";

/** Snapshot aturan satu pembelian berhadiah (`order_gifts.rule`) — bahan hak & cakupan. */
export type OrderGiftRule = GiftMeasureRule & GiftCoverRule;

/**
 * Snapshot SQL (checkout 8680/8780, admin 8830) berkunci SAMA dengan kolom
 * `gift_rules`, jadi dibaca pemeta baris aturan apa adanya — kunci absen =
 * bawaan SQL (ukuran pcs, cakupan produk tunggal, kelipatan ya).
 */
export function mapOrderGiftRule(raw: unknown): OrderGiftRule {
  const r = mapGiftRuleRow(raw && typeof raw === "object" ? raw : {});
  return {
    triggerMinQty: r.triggerMinQty,
    rewardQty: r.rewardQty,
    repeat: r.repeat,
    maxMultiplier: r.maxMultiplier,
    triggerMeasure: r.triggerMeasure,
    triggerMinAmount: r.triggerMinAmount,
    triggerScope: r.triggerScope,
    triggerProductId: r.triggerProductId,
    triggerOptionIds: r.triggerOptionIds,
    triggerTargetType: r.triggerTargetType,
    triggerProductTypeScope: r.triggerProductTypeScope,
    triggerTargets: r.triggerTargets
  };
}

/** Satu baris pesanan, sebatas yang dibaca hak hidup. */
export type GiftLiveRow = {
  orderGiftId?: string | null;
  giftRole?: string | null;
  qty: number;
  unitPrice?: number | null;
  voucherDiscountPerPcs?: number | null;
  keepStatus?: string | null;
  /** Pesanan barisnya berstatus Batal. */
  orderCancelled?: boolean;
  /**
   * Baris HADIAH yang tak bisa diturunkan mesin geser — `!giftRowReducible`.
   * Batas bawah pratinjau penurunan; absen = bisa diturunkan.
   */
  locked?: boolean;
};

export type GiftPurchaseLive = { syaratPcs: number; syaratValue: number; hadiahPcs: number; hak: number };

const whole = (n: unknown): number => {
  const v = Number(n);
  return Number.isFinite(v) ? Math.max(0, Math.floor(v)) : 0;
};

/**
 * Pcs & nilai syarat hidup, pcs hadiah hidup, dan hak satu pembelian. Cermin
 * `gift_purchase_live` (mig 8850) = rumus `order_gift_state`: baris batal dan
 * pesanan Batal tak dihitung; nilai = qty × (harga + potongan voucher per pcs).
 */
export function giftPurchaseLive(
  rule: GiftMeasureRule,
  rows: readonly GiftLiveRow[],
  orderGiftId: string
): GiftPurchaseLive {
  let syaratPcs = 0;
  let syaratValue = 0;
  let hadiahPcs = 0;
  for (const r of rows) {
    if (r.orderGiftId !== orderGiftId || r.keepStatus === "cancelled" || r.orderCancelled) continue;
    const qty = whole(r.qty);
    if (r.giftRole === "syarat") {
      syaratPcs += qty;
      syaratValue += qty * ((Number(r.unitPrice) || 0) + (Number(r.voucherDiscountPerPcs) || 0));
    } else if (r.giftRole === "hadiah") {
      hadiahPcs += qty;
    }
  }
  syaratValue = Math.round(syaratValue);
  return { syaratPcs, syaratValue, hadiahPcs, hak: giftEntitlementMeasure(rule, syaratPcs, syaratValue) };
}

/**
 * Baris hadiah yang masih bisa diturunkan `gift_adjust` (mig 8850, §16.13 butir 6):
 * PO pra-produksi (keep kosong, Menunggu) atau keep Ready Stock belum dibayar —
 * belum lunas, belum diminta kirim, tak duduk di tagihan aktif. Sisanya
 * ditanggung toko saat syaratnya turun (catatan `terkunci`).
 */
export function giftRowReducible(row: {
  keepStatus?: string | null;
  keepSource?: string | null;
  itemStatus?: string | null;
  paidQty?: number | null;
  inActiveInvoice?: boolean;
  shipmentRequested?: boolean;
}): boolean {
  if (whole(row.paidQty) > 0 || row.inActiveInvoice || row.shipmentRequested) return false;
  if (row.keepStatus == null) return row.itemStatus === "Menunggu";
  return row.keepStatus === "unpaid_keep" && row.keepSource === "ready_stock";
}

/** Pcs hadiah hidup satu pembelian yang tak bisa diturunkan (`locked`). */
function lockedHadiahPcs(rows: readonly GiftLiveRow[], orderGiftId: string): number {
  let n = 0;
  for (const r of rows) {
    if (r.orderGiftId !== orderGiftId || r.giftRole !== "hadiah" || !r.locked) continue;
    if (r.keepStatus === "cancelled" || r.orderCancelled) continue;
    n += whole(r.qty);
  }
  return n;
}

/**
 * Pcs hadiah sesudah hak berubah. Cermin `gift_adjust` (mig 8850, §16.13 butir 2):
 * hak NAIK Δ → hadiah + Δ (batas atas — server bisa memberi kurang bila stok,
 * kuota, atau periode aturannya tak cukup); hak TURUN → min(hadiah sebelum,
 * hak sesudah + kelebihan H7). Hadiah yang masih ditutup hak tak dipotong.
 */
export function giftAdjustTarget(input: { hakBefore: number; hadiahBefore: number; hakAfter: number }): number {
  const hakBefore = whole(input.hakBefore);
  const hadiahBefore = whole(input.hadiahBefore);
  const hakAfter = whole(input.hakAfter);
  if (hakAfter > hakBefore) return hadiahBefore + (hakAfter - hakBefore);
  if (hakAfter === hakBefore) return hadiahBefore;
  return Math.min(hadiahBefore, Math.max(0, hakAfter + Math.max(0, hadiahBefore - hakBefore)));
}

/** Satu pembelian berhadiah di pesanan yang ditulis ulang, urut TERTUA dulu. */
export type GiftRebindPurchase = {
  id: string;
  /** Pcs baris syarat LAMA pembelian ini di kunci varian ini (yang ditulis ulang). */
  before: number;
  /** Snapshot aturannya mencakup varian ini. */
  covers: boolean;
};

/**
 * Jatah pcs baru satu kunci varian sesudah tulis-ulang. Cermin `gift_rebind_order`
 * (mig 8850): tetap/bertambah → jatah lama dipertahankan, tambahan ke pembelian
 * TERTUA yang mencakup varian ini; berkurang → pcs tak terikat dulu, lalu
 * pembelian TERBARU dulu. Pembelian yang tak mencakup varian ini → tak terikat.
 */
export function giftRebindQuotas(input: {
  purchases: readonly GiftRebindPurchase[];
  /** Pcs baris lama tak terikat di kunci ini. */
  unbound: number;
  /** Pcs baru kunci ini. */
  n: number;
}): { quotas: Map<string, number>; unbound: number } {
  const n = whole(input.n);
  const u = whole(input.unbound);
  const total = u + input.purchases.reduce((s, p) => s + whole(p.before), 0);
  const quotas = new Map<string, number>();
  let unbound = 0;
  if (n >= total) {
    unbound = u;
    for (const p of input.purchases) {
      if (p.covers) quotas.set(p.id, whole(p.before));
      else unbound += whole(p.before);
    }
    const extra = input.purchases.find((p) => p.covers);
    if (extra) quotas.set(extra.id, (quotas.get(extra.id) ?? 0) + (n - total));
    else unbound += n - total;
    return { quotas, unbound };
  }
  let r = total - n;
  const fromUnbound = Math.min(u, r);
  unbound = u - fromUnbound;
  r -= fromUnbound;
  for (let i = input.purchases.length - 1; i >= 0; i--) {
    const p = input.purchases[i];
    const take = Math.min(whole(p.before), r);
    r -= take;
    if (p.covers) quotas.set(p.id, whole(p.before) - take);
    else unbound += whole(p.before) - take;
  }
  return { quotas, unbound };
}

/** Kunci varian baris — cermin SQL: id varian, selain itu `n:` + nama kecil. */
export function giftRowKey(variantId: string | null | undefined, variantName: string): string {
  const id = (variantId ?? "").trim();
  return id && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(id)
    ? id.toLowerCase()
    : `n:${variantName.trim().toLowerCase()}`;
}

/** Bahan pratinjau satu pembelian berhadiah yang barang syaratnya ditulis ulang. */
export type GiftEditPurchase = {
  id: string;
  name: string;
  rule: OrderGiftRule;
  /** Hak & pcs hadiah hidup SEKARANG (seluruh pesanan). */
  hakBefore: number;
  hadiahBefore: number;
  /** Pcs & nilai syarat di LUAR baris yang ditulis ulang (pesanan lain, baris ber-keep). */
  outsidePcs: number;
  outsideValue: number;
  /** Pcs syarat lama per kunci varian di baris yang ditulis ulang. */
  beforeByKey: ReadonlyMap<string, number>;
  /** Pcs hadiah hidup yang tak bisa diturunkan (lunas/ditagih/dikirim). Absen = 0. */
  hadiahLocked?: number;
};

/** Hasil pratinjau satu pembelian. `naik` = server bisa memberi kurang (stok/kuota/periode). */
export type GiftEditPreview = { id: string; name: string; before: number; after: number; naik: boolean };

/**
 * Pratinjau hadiah Edit Pesanan: baris yang ditulis ulang (lama) → baris baru
 * (kunci varian, pcs, nilai per pcs). Pembagian `giftRebindQuotas` per kunci;
 * sisa pcs tak terikat ikut pembelian TERTUA yang mencakupnya (mig 8860,
 * `gift_auto_attach_order` langkah a); hak sesudahnya dari jatah + syarat di
 * luar, lalu `giftAdjustTarget`. `covers(purchaseId, key)` = snapshot aturan
 * mencakup varian itu. Pembelian BARU dari sisa tak terikat: `giftEditAllocation`
 * (`leftover`) → `giftNewPurchases` (H59, mig 8880).
 */
export function giftEditPreview(input: GiftEditAllocationInput): GiftEditPreview[] {
  return giftEditAllocation(input).previews;
}

export type GiftEditAllocationInput = {
  purchases: readonly GiftEditPurchase[];
  /** Pcs baris lama tak terikat per kunci (yang ditulis ulang). */
  unboundByKey: ReadonlyMap<string, number>;
  lines: ReadonlyArray<{ key: string; qty: number; unitValue: number }>;
  covers: (purchaseId: string, key: string) => boolean;
};

/**
 * `giftEditPreview` + pcs per kunci yang TETAP tak terikat sesudah simpan
 * (tak dicakup / tak distempel pembelian mana pun di pesanan ini) — bahan
 * pratinjau pembelian baru (H59). Tanpa pembelian = seluruh baris editor.
 */
export function giftEditAllocation(input: GiftEditAllocationInput): {
  previews: GiftEditPreview[];
  leftover: Array<{ key: string; qty: number; unitValue: number }>;
} {
  const leftover: Array<{ key: string; qty: number; unitValue: number }> = [];
  const pcs = new Map<string, number>(input.purchases.map((p) => [p.id, p.outsidePcs]));
  const value = new Map<string, number>(input.purchases.map((p) => [p.id, p.outsideValue]));
  const byKey = new Map<string, { n: number; unitValue: number }>();
  for (const l of input.lines) {
    const cur = byKey.get(l.key) ?? { n: 0, unitValue: 0 };
    cur.n += whole(l.qty);
    cur.unitValue = Math.max(cur.unitValue, Number(l.unitValue) || 0);
    byKey.set(l.key, cur);
  }
  // Ukuran nilai: baris berharga 0 (harga menyusul) tak distempel (cermin SQL).
  const stamps = (p: GiftEditPurchase, unitValue: number) =>
    (p.rule.triggerMeasure ?? "pcs") !== "nilai" || unitValue > 0;
  for (const [key, { n, unitValue }] of byKey) {
    const { quotas, unbound } = giftRebindQuotas({
      purchases: input.purchases.map((p) => ({
        id: p.id,
        before: p.beforeByKey.get(key) ?? 0,
        covers: input.covers(p.id, key)
      })),
      unbound: input.unboundByKey.get(key) ?? 0,
      n
    });
    let loose = unbound;
    for (const p of input.purchases) {
      const q = quotas.get(p.id) ?? 0;
      if (!q) continue;
      if (!stamps(p, unitValue)) {
        loose += q;
        continue;
      }
      pcs.set(p.id, (pcs.get(p.id) ?? 0) + q);
      value.set(p.id, (value.get(p.id) ?? 0) + q * unitValue);
    }
    // Sisa tak terikat → pembelian tertua yang mencakup & menstempelnya.
    const joins = loose > 0 ? input.purchases.find((p) => input.covers(p.id, key) && stamps(p, unitValue)) : undefined;
    if (joins) {
      pcs.set(joins.id, (pcs.get(joins.id) ?? 0) + loose);
      value.set(joins.id, (value.get(joins.id) ?? 0) + loose * unitValue);
    } else if (loose > 0) {
      leftover.push({ key, qty: loose, unitValue });
    }
  }
  const previews = input.purchases.map((p) => {
    const hakAfter = giftEntitlementMeasure(p.rule, pcs.get(p.id) ?? 0, Math.round(value.get(p.id) ?? 0));
    const target = giftAdjustTarget({ hakBefore: p.hakBefore, hadiahBefore: p.hadiahBefore, hakAfter });
    const naik = hakAfter > p.hakBefore;
    return {
      id: p.id,
      name: p.name,
      before: p.hadiahBefore,
      // Turun: baris hadiah yang terkunci tetap (cermin `gift_adjust`).
      after: naik ? target : Math.max(target, Math.min(p.hadiahBefore, whole(p.hadiahLocked))),
      naik
    };
  });
  return { previews, leftover };
}

/** Satu baris pesanan pelanggan, sebatas bahan pratinjau. */
export type GiftOrderRow = GiftLiveRow & {
  orderId: string;
  variantId: string | null;
  variantName: string;
};

/** Pembelian berhadiah yang sudah dibaca (`order_gifts`). */
export type GiftPurchaseInfo = { name: string; rule: OrderGiftRule; createdAt: string };

/** Bahan pratinjau Edit Pesanan satu pesanan. */
export type GiftEditContext = {
  purchases: GiftEditPurchase[];
  unboundByKey: Map<string, number>;
};

/**
 * Bahan pratinjau Edit Pesanan satu pesanan. `rewritten(row)` = baris pesanan
 * itu yang ditulis ulang RPC-nya (pembeli & Edit Variasi: keep kosong; Closed
 * PO: Menunggu; Produksi: Produksi) — baris hadiah tak pernah. Pembelian =
 * yang barang syaratnya ada di baris itu, TERTUA dulu (cermin urutan SQL).
 * null = pesanan tanpa barang syarat (tak ada yang perlu dipratinjau).
 * `orderIds` (Gabung Pesanan, mig 8890) = tujuan + duplikat yang ditulis ulang
 * SEKALI JALAN — cermin potret gabungan `gift_merge_rows_before`.
 */
export function giftEditContext(input: {
  orderId: string;
  orderIds?: readonly string[];
  rows: readonly GiftOrderRow[];
  gifts: ReadonlyMap<string, GiftPurchaseInfo>;
  rewritten: (row: GiftOrderRow) => boolean;
}): GiftEditContext | null {
  const orders = new Set(input.orderIds?.length ? input.orderIds : [input.orderId]);
  const mine = input.rows.filter(
    (r) =>
      orders.has(r.orderId) &&
      r.giftRole !== "hadiah" &&
      r.keepStatus !== "cancelled" &&
      !r.orderCancelled &&
      input.rewritten(r)
  );
  const ids = [...new Set(mine.map((r) => (r.giftRole === "syarat" ? r.orderGiftId : null)).filter(Boolean) as string[])]
    .filter((id) => input.gifts.has(id))
    .sort((a, b) => {
      const ga = input.gifts.get(a)!;
      const gb = input.gifts.get(b)!;
      const ta = Date.parse(ga.createdAt);
      const tb = Date.parse(gb.createdAt);
      if (ta !== tb) return (Number.isFinite(ta) ? ta : Infinity) - (Number.isFinite(tb) ? tb : Infinity);
      return a < b ? -1 : a > b ? 1 : 0;
    });
  if (!ids.length) return null;
  const unboundByKey = new Map<string, number>();
  for (const r of mine) {
    if (r.orderGiftId) continue;
    const key = giftRowKey(r.variantId, r.variantName);
    unboundByKey.set(key, (unboundByKey.get(key) ?? 0) + whole(r.qty));
  }
  const purchases = ids.map((id) => {
    const info = input.gifts.get(id)!;
    const live = giftPurchaseLive(info.rule, input.rows, id);
    const beforeByKey = new Map<string, number>();
    let insidePcs = 0;
    let insideValue = 0;
    for (const r of mine) {
      if (r.orderGiftId !== id || r.giftRole !== "syarat") continue;
      const key = giftRowKey(r.variantId, r.variantName);
      const qty = whole(r.qty);
      beforeByKey.set(key, (beforeByKey.get(key) ?? 0) + qty);
      insidePcs += qty;
      insideValue += qty * ((Number(r.unitPrice) || 0) + (Number(r.voucherDiscountPerPcs) || 0));
    }
    return {
      id,
      name: info.name,
      rule: info.rule,
      hakBefore: live.hak,
      hadiahBefore: live.hadiahPcs,
      outsidePcs: Math.max(0, live.syaratPcs - insidePcs),
      outsideValue: Math.max(0, live.syaratValue - Math.round(insideValue)),
      beforeByKey,
      hadiahLocked: lockedHadiahPcs(input.rows, id)
    };
  });
  return { purchases, unboundByKey };
}

/**
 * Cakupan snapshot aturan atas varian baru di pesanan SATU produk (yang memang
 * memuat barang syaratnya). Bertarget / tanpa batas varian = semua varian;
 * berbatas varian = varian yang lolos batas (opsi varian & grupnya dari produk
 * yang dimuat modal). Varian tak dikenal → hanya varian yang sudah jadi syarat
 * pembelian itu (gagal-tutup — server penentunya).
 */
export function giftEditCovers(
  context: GiftEditContext,
  product: {
    variantOptionIds: ReadonlyMap<string, readonly string[]>;
    optionGroupOf: ReadonlyMap<string, string>;
  } | null
): (purchaseId: string, key: string) => boolean {
  const byId = new Map(context.purchases.map((p) => [p.id, p] as const));
  return (purchaseId, key) => {
    const p = byId.get(purchaseId);
    if (!p) return false;
    if (p.rule.triggerScope === "target" || !p.rule.triggerOptionIds.length) return true;
    const optionIds = product?.variantOptionIds.get(key);
    if (optionIds && product) return variantFitsGiftFilter(p.rule.triggerOptionIds, optionIds, product.optionGroupOf);
    return (p.beforeByKey.get(key) ?? 0) > 0;
  };
}

/**
 * Pratinjau hadiah sesudah pcs baris berubah TANPA tulis-ulang: Ubah Pcs admin
 * (`admin_set_order_item_qty`) dan batal atas permintaan pembeli
 * (`admin_cancel_order_items_by`). `changes` = id baris → pcs sesudahnya
 * (0 = dibatalkan penuh). Hanya pembelian yang barang SYARAT-nya berubah yang
 * digeser (cermin potret mesin batal); baris hadiah yang ikut dibatalkan dalam
 * aksi yang sama dihitung, tak pernah dikembalikan, dan baris hadiah terkunci
 * tak ikut turun (cermin `gift_adjust`).
 *
 * `joins` (Ubah Pcs, Hadiah Tahap 6-2) = baris TAK TERIKAT yang ikut pembelian
 * di pesanan yang sama sesudah simpan (mesin langkah a — `adminGiftOrderJoins`):
 * id baris → id pembelian. Pembelian itu ikut digeser; barisnya dihitung
 * sebagai syaratnya pada keadaan sesudah.
 */
export function giftQtyPreview(input: {
  rows: ReadonlyArray<GiftOrderRow & { itemId: string }>;
  gifts: ReadonlyMap<string, GiftPurchaseInfo>;
  changes: ReadonlyMap<string, number>;
  joins?: ReadonlyMap<string, string>;
}): GiftEditPreview[] {
  const touched = new Set<string>();
  for (const r of input.rows) {
    if (r.giftRole === "syarat" && r.orderGiftId && input.changes.has(r.itemId)) touched.add(r.orderGiftId);
  }
  for (const id of input.joins?.values() ?? []) touched.add(id);
  const after = input.rows.map((r) => {
    const join = input.joins?.get(r.itemId);
    const row = join && !r.orderGiftId && !r.giftRole ? { ...r, orderGiftId: join, giftRole: "syarat" } : r;
    const next = input.changes.get(r.itemId);
    if (next === undefined) return row;
    return whole(next) > 0 ? { ...row, qty: whole(next) } : { ...row, keepStatus: "cancelled" };
  });
  const out: GiftEditPreview[] = [];
  for (const id of touched) {
    const info = input.gifts.get(id);
    if (!info) continue;
    const before = giftPurchaseLive(info.rule, input.rows, id);
    const now = giftPurchaseLive(info.rule, after, id);
    const target = giftAdjustTarget({ hakBefore: before.hak, hadiahBefore: before.hadiahPcs, hakAfter: now.hak });
    const naik = now.hak > before.hak;
    out.push({
      id,
      name: info.name,
      before: before.hadiahPcs,
      // Turun: baris hadiah yang terkunci tetap (cermin `gift_adjust`).
      after: naik
        ? now.hadiahPcs + (now.hak - before.hak)
        : Math.max(Math.min(now.hadiahPcs, target), lockedHadiahPcs(after, id)),
      naik
    });
  }
  return out;
}

/**
 * Kalimat pratinjau pembelian berhadiah BARU (H59, mig 8880) — modal Edit
 * Pesanan pembeli, Edit Variasi, Ubah Pcs & Gabung Pesanan admin — atau
 * hadiah yang bertambah di pembelian LAMA (`old`, langkah a', Tahap 6-2).
 * Tanpa kata ganti (dua pembaca).
 */
export function giftNewPurchaseText(p: { name: string; pcs: number; old?: boolean }): string {
  if (p.old) {
    const label = p.name ? `Hadiah "${p.name}"` : "Hadiah";
    return `${label} di pesanan sebelumnya bertambah ${p.pcs} pcs, selama stok hadiahnya masih ada.`;
  }
  const label = p.name ? `hadiah "${p.name}"` : "hadiah";
  return `Pesanan ini dapat ${label} ${p.pcs} pcs, selama stok hadiahnya masih ada.`;
}

/** Kalimat pratinjau satu pembelian (modal Edit Pesanan / Ubah Pcs / Batalkan). */
export function giftEditPreviewText(p: Pick<GiftEditPreview, "name" | "before" | "after" | "naik">): string {
  const label = p.name ? `Hadiah "${p.name}"` : "Hadiah";
  if (p.after === p.before) return `${label} tetap ${p.before} pcs.`;
  if (p.after > p.before) return `${label} bertambah jadi ${p.after} pcs, selama stok hadiahnya masih ada.`;
  return `${label} ikut berkurang jadi ${p.after} pcs.`;
}

/** Penggeseran terakhir (`order_gifts.adjust_note`, mig 8850). */
export type GiftAdjustNote = {
  at: string | null;
  source: string | null;
  added: number;
  removed: number;
  short: number;
  reason: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftAdjustNote(raw: any): GiftAdjustNote | null {
  if (!raw || typeof raw !== "object") return null;
  return {
    at: typeof raw.at === "string" ? raw.at : null,
    source: typeof raw.source === "string" ? raw.source : null,
    added: whole(raw.added),
    removed: whole(raw.removed),
    short: whole(raw.short),
    reason: typeof raw.reason === "string" ? raw.reason : null
  };
}

const KURANG_SEBAB: Record<string, string> = {
  stok: "stok hadiahnya habis",
  kuota: "kuota hadiahnya sudah habis",
  aturan: "promo hadiahnya sudah berakhir",
  level: "level akun tidak lagi termasuk promo ini",
  produk: "produk hadiahnya sedang tidak bisa dipesan"
};

/**
 * Keterangan hadiah yang KURANG dari haknya sesudah jumlah barang berubah (H42).
 * Hanya tampil selama kekurangannya masih ada (hak hidup > hadiah hidup) dan
 * catatan terakhirnya memang penambahan yang tak penuh. null = tak ada keterangan.
 */
export function giftAdjustNoteText(input: {
  name: string | null;
  note: GiftAdjustNote | null;
  live: Pick<GiftPurchaseLive, "hak" | "hadiahPcs">;
}): string | null {
  const { note, live } = input;
  if (!note || note.short <= 0 || !note.reason || note.reason === "terkunci") return null;
  const kurang = live.hak - live.hadiahPcs;
  if (kurang <= 0) return null;
  const sebab = KURANG_SEBAB[note.reason];
  if (!sebab) return null;
  const label = input.name ? `Hadiah "${input.name}"` : "Hadiah";
  return `${label} kurang ${Math.min(kurang, note.short)} pcs karena ${sebab}.`;
}

// ─── Lengkapi Hadiah (Tahap 5-2, mig 8870) ──────────────────────────────────
//
// `admin_fill_order_gift` mengisi kekurangan yang dilaporkan `giftAdjustNoteText`
// (H50: syarat tombol = syarat kalimat "kurang") lewat jalur naik `gift_adjust`.

/** Hasil `admin_fill_order_gift`: pcs yang diminta, yang benar-benar masuk, dan sebab sisanya. */
export type GiftFillResult = {
  name: string | null;
  requested: number;
  added: number;
  reason: string | null;
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapGiftFillResult(raw: any): GiftFillResult {
  const r = raw && typeof raw === "object" ? raw : {};
  return {
    name: typeof r.name === "string" && r.name.trim() ? r.name.trim() : null,
    requested: whole(r.requested),
    added: whole(r.added),
    reason: typeof r.reason === "string" ? r.reason : null
  };
}

/** Kalimat sesudah tombol Lengkapi Hadiah ditekan (penuh / sebagian / belum bisa). */
export function giftFillResultText(r: GiftFillResult): string {
  const label = r.name ? `Hadiah "${r.name}"` : "Hadiah";
  const sebab = (r.reason && KURANG_SEBAB[r.reason]) || null;
  if (r.added > 0 && r.added >= r.requested) return `${label} dilengkapi ${r.added} pcs.`;
  if (r.added > 0) {
    return sebab
      ? `${label} baru bisa ditambah ${r.added} pcs karena ${sebab}.`
      : `${label} baru bisa ditambah ${r.added} pcs.`;
  }
  return sebab ? `${label} belum bisa dilengkapi karena ${sebab}.` : `${label} belum bisa dilengkapi.`;
}
